// File: src/components/EntrantListView.js
// The organizer's view of who has entered, and the download their timing
// operator needs.
//
// Contact details are shown here and nowhere else public: an organizer has to
// be able to reach an entrant. The manage token is never returned by the API,
// so it cannot appear here even by accident.

import React, { useState, useEffect, useCallback } from 'react';
import { X, Download, Users, Trash2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { apiGet, apiDelete, apiDownload } from '../services/api';

// Must match EXPORT_FORMATS in backend/entry-export.js, including which file
// each system is normally imported from.
const TIMING_SYSTEMS = [
  { value: 'myrcm', label: 'MyRCM', defaultType: 'xlsx' },
  { value: 'liverc', label: 'LiveRC', defaultType: 'csv' }
];

const STATUS_CLASSES = {
  registered: 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400',
  waitlisted: 'bg-yellow-100 dark:bg-yellow-500/20 text-yellow-800 dark:text-yellow-200',
  cancelled: 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400'
};

function EntrantListView({ event, token, onClose }) {
  const { t } = useLanguage();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [timingSystem, setTimingSystem] = useState('myrcm');
  const [downloading, setDownloading] = useState('');

  // The extras columns come from the entries themselves rather than a second
  // fetch of the event: every answer row carries its question's label and type,
  // and an event that asks nothing produces no columns at all.
  //
  // Ordered by first appearance, which is the order the server sent the answers
  // in - the organizer's own sort_order.
  const questionColumns = [];
  const seenQuestions = new Set();
  for (const entry of entries) {
    for (const answer of entry.answers || []) {
      if (seenQuestions.has(answer.question_id)) continue;
      seenQuestions.add(answer.question_id);
      questionColumns.push({
        id: answer.question_id, label: answer.label, answer_type: answer.answer_type
      });
    }
  }

  // What the organizer actually needs: how many lunches to order, how many tyre
  // sets to bring. A yes/no totals the number of yeses, because yes is 1 - the
  // same SUM either way. Free text has no total; it is a list, not a quantity.
  //
  // Cancelled entries are excluded: someone who withdrew does not want lunch.
  const answerTotals = new Map();
  for (const entry of entries) {
    if (entry.status === 'cancelled') continue;
    for (const answer of entry.answers || []) {
      if (answer.answer_type === 'text' || answer.answer_number === null) continue;
      answerTotals.set(answer.question_id,
        (answerTotals.get(answer.question_id) || 0) + answer.answer_number);
    }
  }

  // One entrant's answer, as a cell. A blank is a question they were not asked
  // or chose not to answer - there is no row either way.
  const answerText = (entry, question) => {
    const answer = (entry.answers || []).find(a => a.question_id === question.id);
    if (!answer) return '';
    if (question.answer_type === 'text') return answer.answer_text || '';
    if (question.answer_type === 'yes_no') return answer.answer_number === 1 ? '✓' : '';
    return answer.answer_number === null ? '' : String(answer.answer_number);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const query = statusFilter ? `?status=${statusFilter}` : '';
      setEntries(await apiGet(`/events/${event.id}/entries${query}`, { token }));
      setError('');
    } catch (err) {
      setError((err.body && err.body.message) || err.message || t('entrantFailedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [event.id, statusFilter, token, t]);

  useEffect(() => { load(); }, [load]);

  const download = async (type) => {
    setDownloading(type);
    setError('');
    try {
      await apiDownload(
        `/events/${event.id}/entries/export?format=${timingSystem}&type=${type}`,
        { token, fallbackFilename: `entries.${type}` }
      );
    } catch (err) {
      setError((err.body && err.body.message) || err.message || t('entrantExportFailed'));
    } finally {
      setDownloading('');
    }
  };

  const removeEntry = async (entry) => {
    if (!window.confirm(t('entrantRemoveConfirm'))) return;
    try {
      await apiDelete(`/events/${event.id}/entries/${entry.id}`, { token });
      await load();
    } catch (err) {
      setError((err.body && err.body.message) || err.message);
    }
  };

  const recommended =
    (TIMING_SYSTEMS.find(s => s.value === timingSystem) || {}).defaultType || 'xlsx';

  const statusLabel = (status) => ({
    registered: t('entryStatusRegistered'),
    waitlisted: t('entryStatusWaitlisted'),
    cancelled: t('entryStatusCancelled')
  }[status] || status);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-paper rounded-lg p-6 max-w-5xl w-full max-h-[90vh] overflow-y-auto border border-line">
        <div className="flex justify-between items-start mb-4 gap-4">
          <div>
            <h3 className="text-xl font-bold text-ink">{t('entrantList')}</h3>
            <p className="text-ink-muted text-sm mt-1">
              {event.name} · {entries.length} {t('entrantCount')}
            </p>
          </div>
          <button onClick={onClose} className="text-ink-muted hover:text-ink" title={t('close')}>
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Download panel */}
        <div className="bg-paper-alt rounded p-4 mb-4">
          <h4 className="text-ink font-semibold mb-3 flex items-center gap-2">
            <Download className="w-4 h-4" />
            {t('entrantExport')}
          </h4>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[12rem]">
              <label className="block text-ink-muted mb-2 font-semibold text-sm">
                {t('entrantExportFormat')}
              </label>
              <select
                value={timingSystem}
                onChange={(e) => setTimingSystem(e.target.value)}
                className="w-full px-4 py-2 bg-paper border border-line rounded text-ink"
              >
                {TIMING_SYSTEMS.map(system => (
                  <option key={system.value} value={system.value}>{system.label}</option>
                ))}
              </select>
            </div>
            {/* The system's own format leads; the other is still there for an
                organizer who wants to look at the list in Excel. */}
            {(recommended === 'xlsx'
              ? ['xlsx', 'csv'] : ['csv', 'xlsx']).map((type, index) => (
              <button
                key={type}
                onClick={() => download(type)}
                disabled={downloading !== ''}
                className={index === 0
                  ? 'bg-accent hover:opacity-90 disabled:opacity-50 text-accent-ink px-4 py-2 rounded font-semibold'
                  : 'bg-paper hover:opacity-80 disabled:opacity-50 text-ink px-4 py-2 rounded font-semibold border border-line'}
              >
                {type === 'xlsx' ? t('entrantExportExcel') : t('entrantExportCsv')}
              </button>
            ))}
          </div>

        </div>

        <div className="flex gap-2 mb-4">
          {['', 'registered', 'waitlisted', 'cancelled'].map(status => (
            <button
              key={status || 'all'}
              onClick={() => setStatusFilter(status)}
              className={`px-3 py-1.5 rounded text-sm font-semibold ${
                statusFilter === status ? 'bg-accent text-accent-ink' : 'bg-paper-alt text-ink-muted hover:text-ink'
              }`}
            >
              {status === '' ? t('entrantFilterAll') : statusLabel(status)}
            </button>
          ))}
        </div>

        {error && (
          <div className="bg-red-50 dark:bg-red-500/20 border border-red-500 text-red-800 dark:text-red-200 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* The number the organizer rings the caterer with. Above the table
            rather than in a footer row, because it is the reason they opened
            this screen. */}
        {answerTotals.size > 0 && (
          <div className="bg-paper-alt rounded-lg px-4 py-3 mb-4 flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="text-ink-muted font-semibold">{t('entrantExtrasTotals')}:</span>
            {questionColumns
              .filter(question => answerTotals.has(question.id))
              .map(question => (
                <span key={question.id} className="text-ink">
                  {question.label}{' '}
                  <span className="font-mono-num font-semibold">
                    {answerTotals.get(question.id)}
                  </span>
                </span>
              ))}
          </div>
        )}

        {loading ? (
          <p className="text-ink-muted">{t('loading')}</p>
        ) : entries.length === 0 ? (
          <div className="bg-paper-alt rounded-lg p-10 text-center">
            <Users className="w-10 h-10 text-ink-muted mx-auto mb-3" />
            <p className="text-ink-muted">{t('entrantNone')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="text-ink-muted border-b border-line">
                  <th className="py-2 pr-3 font-semibold">{t('name')}</th>
                  <th className="py-2 pr-3 font-semibold">{t('eventClub')}</th>
                  <th className="py-2 pr-3 font-semibold">
                    {t('eventClasses')} &amp; {t('entrantTransponder')}
                  </th>
                  {questionColumns.map(question => (
                    <th key={question.id} className="py-2 pr-3 font-semibold whitespace-nowrap">
                      {question.label}
                    </th>
                  ))}
                  <th className="py-2 pr-3 font-semibold">{t('entrantContact')}</th>
                  <th className="py-2 pr-3 font-semibold">{t('entrantStatus')}</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {entries.map(entry => (
                  <tr key={entry.id} className="border-b border-line last:border-0 align-top">
                    <td className="py-2 pr-3 text-ink">
                      {entry.first_name} {entry.last_name}
                      {entry.car_number ? (
                        <span className="text-ink-muted font-mono-num"> #{entry.car_number}</span>
                      ) : null}
                    </td>
                    <td className="py-2 pr-3 text-ink-muted">{entry.club || ''}</td>
                    {/* One class per line with its own transponder: a driver
                        running two cars has two numbers, and the timing
                        operator needs to see which goes with which. */}
                    <td className="py-2 pr-3 text-ink-muted">
                      {entry.classes.map(cls => (
                        <div key={cls.id} className="whitespace-nowrap">
                          {cls.name}
                          {cls.transponder_number ? (
                            <span className="font-mono-num"> · {cls.transponder_number}</span>
                          ) : (
                            <span className="text-xs"> · {t('entrantNoTransponder')}</span>
                          )}
                        </div>
                      ))}
                    </td>
                    {questionColumns.map(question => (
                      <td
                        key={question.id}
                        className={`py-2 pr-3 text-ink-muted${
                          question.answer_type === 'number' ? ' font-mono-num' : ''}`}
                      >
                        {answerText(entry, question)}
                      </td>
                    ))}
                    <td className="py-2 pr-3 text-ink-muted">
                      {entry.email && <div className="break-all">{entry.email}</div>}
                      {entry.phone && <div>{entry.phone}</div>}
                    </td>
                    <td className="py-2 pr-3">
                      <span className={`text-xs font-semibold px-2 py-1 rounded whitespace-nowrap ${STATUS_CLASSES[entry.status] || ''}`}>
                        {statusLabel(entry.status)}
                      </span>
                    </td>
                    <td className="py-2">
                      <button
                        onClick={() => removeEntry(entry)}
                        className="text-red-700 dark:text-red-400 hover:opacity-80"
                        title={t('entrantRemove')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default EntrantListView;
